import * as React from "react";
import { Breakpoint } from "@material-ui/core/styles/createBreakpoints";
import {
    FormControl,
    FormHelperText,
    InputLabel
} from "./widget"
import { GridSize } from "@material-ui/core/Grid";
import { OnChange, OnCompletion } from "../view/component"
import { Validator } from "../validation/validator"
import { FieldErrors } from "../validation/validatableentity"
import { useif } from "components/state/types";

type ValueType = boolean | string | string[] | number | Date;

type FieldGetter<VT> = (
    field: string) => VT;

type FieldSetter<VT> = (
    field: string,
    value: VT,
    validator?: Validator<VT>,
    storeInvalidValue?: boolean) => void;

interface EntityExchange {
    name?: string;
    getter?: FieldGetter<any>,
    setter?: FieldSetter<any>,
    validator?: Validator<any>
    error?: string | FieldErrors;
}

const bind = (entitylink: EntityExchange & Binding): Binding => {
    const { name, getter, setter, validator } = entitylink;

    let value = null;
    if (getter != null) {
        value = getter(name);
    } else if (entitylink.value == null) {
        value = "";
    } else {
        value = entitylink.value;
    }

    const onChange =
        entitylink.onChange ??
        useif(
            setter,
            (value: any) => setter(name, value, validator, true)
        );

    return {
        value,
        onChange
    }
}

interface Binding {
    value?: ValueType;
    onChange?: OnChange;
}

interface Data extends Binding {
}


interface Display {
    defaultValue?: ValueType;
    label?: string;
    note?: string;
    placeholder?: string;
}

interface BehaviorAndLook {
    disabled?: boolean;
    autoFocus?: boolean;
    hideLabel?: boolean;
    fullWidth?: boolean;
    singleSelect?: boolean;
    onBlur?: OnCompletion;
}

interface ControlProps extends
    BehaviorAndLook,
    Display,
    Data,
    EntityExchange,
    Partial<Record<Breakpoint, boolean | GridSize>> {
    children?: React.ReactNode
}

interface ControlWidgetProps extends
    Data,
    EntityExchange {
}


const getError = (error: string | FieldErrors): React.ReactNode => {
    const { errors } = error as FieldErrors;
    if (errors) {
        return <>
            {Array.isArray(errors) ? errors.map((e, k) => <div key={k}>{e}</div>) : errors}
        </>;
    } else {
        return error;
    }
}

const Control = (props: ControlProps) => {

    const { name, label, note, error } = props;

    let showLabel = props.hideLabel == null || !props.hideLabel;
    const id = name;
    const hasError = error != null;
    const helperText = hasError ? getError(error) : note;
    const hasText = helperText != null;
    const helperTextId = hasText ? id + "-text" : null;

    return (
        <FormControl fullWidth error={hasError} aria-describedby={helperTextId} disabled={props.disabled}>
            {showLabel && <InputLabel shrink htmlFor={id}>{label}</InputLabel>}
            {props.children}
            {hasText && <FormHelperText component="div" id={helperTextId}>{helperText}</FormHelperText>}
        </FormControl>
    );
}

export {
    bind,
    Control,
    ControlProps,
    ControlWidgetProps,
    FieldGetter,
    FieldSetter
}